/**
 * Bills Page
 *
 * Mobile-first bill list with cards on mobile, table on desktop.
 */

import { useState, useCallback, useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Search, FileText, ChevronRight, CheckCircle2, XCircle, Clock, AlertCircle, Pencil, ShoppingCart, BarChart3, Users, RefreshCw } from 'lucide-react'

import api from '@/lib/api'
import { cn, formatCurrency, formatDate, BILL_STATUS_LABELS, BILL_TYPE_LABELS, debounce, formatClientWithNoteForExport } from '@/lib/utils'
import { BillType, BillStatus, HACIENDA_STATUS_LABELS, HACIENDA_STATUS_COLORS } from '@/types/bill'
import { t } from '@/i18n'
import { useAuth } from '@/hooks/useAuth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { ExportButtons } from '@/components/ui/export-buttons'
import { Pagination } from '@/components/ui/pagination'
import { DatePicker } from '@/components/ui/date-picker'
import { useToast } from '@/components/ui/toaster'

interface Bill {
  id: number
  bill_number: string
  bill_type: string
  client_name: string
  notes?: string | null
  created_at: string
  total: number
  status: string
  payment_type?: string
  hacienda_status?: string
}

const getStatusBadgeVariant = (status: string) => {
  switch (status) {
    case 'paid':
    case 'finalized':
      return 'success'
    case 'credit':
      return 'warning'
    case 'cancelled':
      return 'destructive'
    default:
      return 'secondary'
  }
}

const getHaciendaStatusIcon = (status?: string) => {
  switch (status) {
    case 'accepted':
    case 'success':
      return <CheckCircle2 className="h-3.5 w-3.5" />
    case 'rejected':
    case 'failed':
      return <XCircle className="h-3.5 w-3.5" />
    case 'error':
      return <XCircle className="h-3.5 w-3.5" />
    case 'pending':
      return <Clock className="h-3.5 w-3.5" />
    case 'submitted':
      return <Clock className="h-3.5 w-3.5" />
    default:
      return <AlertCircle className="h-3.5 w-3.5" />
  }
}

const getHaciendaStatusBadge = (status?: string) => {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-800/50 dark:text-gray-400">
        <AlertCircle className="h-3 w-3" />
        <span className="hidden sm:inline">{t('bills.haciendaNotSent')}</span>
      </span>
    )
  }
  const label = HACIENDA_STATUS_LABELS[status] || status
  const colors = HACIENDA_STATUS_COLORS[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-400'
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium ${colors}`}>
      {getHaciendaStatusIcon(status)}
      {label}
    </span>
  )
}

export function BillsPage() {
  const navigate = useNavigate()
  const { isAccountant } = useAuth()
  const [searchParams] = useSearchParams()
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [haciendaFilter, setHaciendaFilter] = useState<string>('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  // Read filter from URL on mount
  useEffect(() => {
    const filter = searchParams.get('filter')
    if (filter) {
      setStatusFilter(filter)
    }
    const hacienda = searchParams.get('hacienda')
    if (hacienda) {
      setHaciendaFilter(hacienda)
    }
  }, [searchParams])

  // Debounced search
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearchQuery(value)
      setPage(1)
    }, 300),
    []
  )

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSearch(value)
  }

  // Pone al día los documentos que siguen esperando a Hacienda: sin esto el
  // estado solo cambiaba al abrir cada uno, y un rechazo pasaba inadvertido.
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const { data: pendingSync, isFetching: syncingHacienda } = useQuery({
    queryKey: ['hacienda-sync-pending'],
    queryFn: async () => {
      const response = await api.post('/invoicing/documents/sync-pending')
      return response.data as { checked: number; updated: number; rejected: number }
    },
    staleTime: 2 * 60 * 1000,
    retry: false,
  })
  useEffect(() => {
    if (!pendingSync?.updated) return
    queryClient.invalidateQueries({ queryKey: ['bills'] })
    if (pendingSync.rejected > 0) {
      toast({
        title: t('invoicing.rejectedFound', { count: pendingSync.rejected }),
        description: t('invoicing.rejectedFoundHint'),
        variant: 'destructive',
      })
    }
  }, [pendingSync, queryClient, toast])

  // Fetch bills (excluding proformas - they have their own page)
  const { data, isLoading } = useQuery({
    queryKey: ['bills', page, searchQuery, statusFilter, haciendaFilter, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams()
      params.set('page', page.toString())
      params.set('page_size', '20')
      params.set('exclude_type', 'proforma')
      if (searchQuery) {
        params.set('search', searchQuery)
      }
      if (statusFilter) {
        params.set('status_filter', statusFilter)
      }
      if (haciendaFilter) {
        params.set('hacienda_status', haciendaFilter)
      }
      if (dateFrom) {
        params.set('date_from', `${dateFrom}T00:00:00`)
      }
      if (dateTo) {
        params.set('date_to', `${dateTo}T23:59:59`)
      }
      const response = await api.get(`/bills/?${params}`)
      return response.data
    },
  })

  return (
    <div className="space-y-4">
      {/* Quick Actions - Mobile */}
      <div className={cn('grid gap-2 md:hidden', isAccountant ? 'grid-cols-2' : 'grid-cols-3')}>
        {!isAccountant && (
          <Button
            asChild
            className="h-auto py-3 flex-col gap-1 bg-primary hover:bg-primary/90"
          >
            <Link to="/bills/new">
              <ShoppingCart className="h-5 w-5" />
              <span className="text-xs font-medium">{t('bills.newBill')}</span>
            </Link>
          </Button>
        )}
        <Button
          asChild
          variant="outline"
          className="h-auto py-3 flex-col gap-1"
        >
          <Link to="/reports">
            <BarChart3 className="h-5 w-5" />
            <span className="text-xs">{t('nav.reports')}</span>
          </Link>
        </Button>
        <Button
          asChild
          variant="outline"
          className="h-auto py-3 flex-col gap-1"
        >
          <Link to="/clients">
            <Users className="h-5 w-5" />
            <span className="text-xs">{t('nav.clients')}</span>
          </Link>
        </Button>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl md:text-3xl font-bold flex items-center gap-2">
            {t('bills.title')}
            {syncingHacienda && (
              <RefreshCw
                className="h-4 w-4 animate-spin text-muted-foreground"
                role="status"
                aria-label={t('invoicing.checkingHacienda')}
              />
            )}
          </h1>
          <p className="text-sm text-muted-foreground hidden md:block">
            {t('bills.countLabel', { count: data?.total || 0 })}
          </p>
        </div>
        <div className="flex gap-2">
          <ExportButtons
            data={(data?.items || []).map((b: Bill) => ({
              ...b,
              client_name: formatClientWithNoteForExport(b.client_name, b.notes, t('bills.generalClient')),
            }))}
            filename="facturas"
            columns={[
              { key: 'bill_number', header: t('bills.billNumber'), format: (v) => v ? String(v) : '' },
              { key: 'bill_type', header: t('bills.type'), format: (v) => BILL_TYPE_LABELS[v as BillType] || String(v) },
              { key: 'client_name', header: t('bills.client'), format: (v) => v ? String(v) : t('bills.generalClient') },
              { key: 'created_at', header: t('bills.date'), format: (v) => formatDate(v as string) },
              { key: 'total', header: t('bills.total'), format: (v) => formatCurrency(v as number) },
              { key: 'status', header: t('bills.status'), format: (v) => BILL_STATUS_LABELS[v as BillStatus] || String(v) },
            ]}
          />
          {!isAccountant && (
            <Button asChild className="hidden md:flex" size="lg">
              <Link to="/bills/new">
                <ShoppingCart className="h-5 w-5 mr-2" />
                {t('bills.newBill')}
              </Link>
            </Button>
          )}
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
        <Input
          placeholder={t('bills.searchPlaceholder')}
          className="pl-10 h-12 text-base"
          value={searchInput}
          onChange={(e) => handleSearchChange(e.target.value)}
        />
      </div>

      {/* Filters: 2 cols on mobile, 4 cols on desktop */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 md:gap-3">
        <select
          aria-label={t('bills.allStatuses')}
          className="h-12 px-3 rounded-md border border-input bg-background text-sm md:text-base min-w-0"
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value)
            setPage(1)
          }}
        >
          <option value="">{t('bills.allStatuses')}</option>
          <option value="pending">{t('bills.filterPending')}</option>
          <option value="paid">{t('bills.filterPaid')}</option>
          <option value="credit">{t('bills.filterCredit')}</option>
          <option value="cancelled">{t('bills.filterCancelled')}</option>
        </select>
        <select
          aria-label={t('bills.allHacienda')}
          className="h-12 px-3 rounded-md border border-input bg-background text-sm md:text-base min-w-0"
          value={haciendaFilter}
          onChange={(e) => {
            setHaciendaFilter(e.target.value)
            setPage(1)
          }}
        >
          <option value="">{t('bills.allHacienda')}</option>
          <option value="accepted">{t('bills.haciendaSuccess')}</option>
          <option value="submitted">{t('bills.submittedToHacienda')}</option>
          <option value="pending">{t('bills.haciendaPending')}</option>
          <option value="error">{t('bills.submitError')}</option>
          <option value="rejected">{t('bills.haciendaFailed')}</option>
          <option value="not_sent">{t('bills.haciendaNotSent')}</option>
        </select>
        <DatePicker
          value={dateFrom}
          onChange={(v) => {
            setDateFrom(v)
            setPage(1)
          }}
          max={dateTo || undefined}
          placeholder={t('bills.dateFrom')}
          ariaLabel={t('bills.dateFrom')}
        />
        <DatePicker
          value={dateTo}
          onChange={(v) => {
            setDateTo(v)
            setPage(1)
          }}
          min={dateFrom || undefined}
          placeholder={t('bills.dateTo')}
          ariaLabel={t('bills.dateTo')}
        />
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      ) : data?.items?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12">
          <FileText className="h-12 w-12 text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium">{t('bills.noBills')}</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {searchQuery || statusFilter || haciendaFilter || dateFrom || dateTo
              ? t('bills.noResults')
              : t('bills.createFirst')}
          </p>
          {!isAccountant && !searchQuery && !statusFilter && !haciendaFilter && !dateFrom && !dateTo && (
            <Button asChild>
              <Link to="/bills/new">
                <Plus className="h-4 w-4 mr-2" />
                {t('bills.newBill')}
              </Link>
            </Button>
          )}
        </div>
      ) : (
        <>
          {/* Mobile: Cards */}
          <div className="space-y-3 lg:hidden">
            {data?.items?.map((bill: Bill) => (
              <Link
                key={bill.id}
                to={bill.status === 'pending' ? `/bills/new?continue=${bill.id}` : `/bills/${bill.id}`}
                className="block rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
              <Card className="active:bg-muted/50 touch-manipulation">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
                        <p className="font-medium whitespace-nowrap">
                          {bill.bill_number || `#${bill.id}`}
                        </p>
                        <Badge variant={getStatusBadgeVariant(bill.status) as 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'}>
                          {BILL_STATUS_LABELS[bill.status as BillStatus] || bill.status}
                        </Badge>
                        {getHaciendaStatusBadge(bill.hacienda_status)}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {bill.client_name || t('bills.generalClient')}
                      </p>
                      {bill.notes && (
                        <p className="text-xs text-muted-foreground italic break-words mt-0.5">
                          &ldquo;{bill.notes}&rdquo;
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        {formatDate(bill.created_at)} - {BILL_TYPE_LABELS[bill.bill_type as BillType] || bill.bill_type}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      {bill.status === 'pending' && (
                        <Pencil className="h-4 w-4 text-primary" />
                      )}
                      <p className="font-bold text-primary text-lg">
                        {formatCurrency(bill.total)}
                      </p>
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </div>
                  </div>
                </CardContent>
              </Card>
              </Link>
            ))}
          </div>

          {/* Desktop: Table */}
          <Card className="hidden lg:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[760px]">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left p-4 font-medium whitespace-nowrap">{t('bills.billNumber')}</th>
                    <th className="text-left p-4 font-medium whitespace-nowrap">{t('bills.type')}</th>
                    <th className="text-left p-4 font-medium">{t('bills.client')}</th>
                    <th className="text-left p-4 font-medium whitespace-nowrap">{t('bills.date')}</th>
                    <th className="text-right p-4 font-medium whitespace-nowrap">{t('bills.total')}</th>
                    <th className="text-left p-4 font-medium whitespace-nowrap">{t('bills.status')}</th>
                    <th className="text-center p-4 font-medium whitespace-nowrap">Hacienda</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items?.map((bill: Bill) => (
                    <tr
                      key={bill.id}
                      className="border-b hover:bg-muted/50 cursor-pointer focus-visible:outline-none focus-visible:bg-muted/50 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key !== 'Enter') return
                        navigate(bill.status === 'pending' ? `/bills/new?continue=${bill.id}` : `/bills/${bill.id}`)
                      }}
                      onClick={() =>
                        bill.status === 'pending'
                          ? navigate(`/bills/new?continue=${bill.id}`)
                          : navigate(`/bills/${bill.id}`)
                      }
                      title={
                        bill.status === 'pending'
                          ? t('bills.continuePending')
                          : t('common.view')
                      }
                    >
                      <td className="p-4 font-medium whitespace-nowrap">
                        <span className="inline-flex items-center gap-2">
                          {bill.status === 'pending' && (
                            <Pencil className="h-3.5 w-3.5 text-primary shrink-0" />
                          )}
                          {bill.bill_number || `#${bill.id}`}
                        </span>
                      </td>
                      <td className="p-4 whitespace-nowrap">
                        {BILL_TYPE_LABELS[bill.bill_type as BillType] || bill.bill_type}
                      </td>
                      <td className="p-4">
                        <div className="flex flex-col">
                          <span>{bill.client_name || t('bills.generalClient')}</span>
                          {bill.notes && (
                            <span className="text-xs text-muted-foreground italic">
                              &ldquo;{bill.notes}&rdquo;
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="p-4 whitespace-nowrap">{formatDate(bill.created_at)}</td>
                      <td className="p-4 text-right font-medium text-primary whitespace-nowrap">
                        {formatCurrency(bill.total)}
                      </td>
                      <td className="p-4">
                        <Badge variant={getStatusBadgeVariant(bill.status) as 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'warning'}>
                          {BILL_STATUS_LABELS[bill.status as BillStatus] || bill.status}
                        </Badge>
                      </td>
                      <td className="p-4 text-center">
                        {getHaciendaStatusBadge(bill.hacienda_status)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Pagination */}
          {data && data.pages > 1 && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
              <p className="text-sm text-muted-foreground">
                {t('common.page')} {data.page} {t('common.of')} {data.pages}
              </p>
              <Pagination
                currentPage={page}
                totalPages={data.pages}
                onPageChange={setPage}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}
